import { Context } from '../index';

export const getStatsByDay = (context: Context, { pubId, params }: { pubId: any; params: any }) => {
  context.actions.publisher.checkAllSites({ params });
  const endpoint = `/api/v0/publishers/${pubId}/stats_by_day/`;
  return context.effects.api.get(endpoint, params).then(resp => {
    return resp.data.data[0].stats;
  });
};

export const getStatsByMonth = (
  context: Context,
  { pubId, params = {} }: { pubId: any; params: any },
) => {
  context.actions.publisher.checkAllSites({ params });
  const endpoint = `/api/v0/publishers/${pubId}/stats_by_month/`;
  return context.effects.api.get(endpoint, params).then(resp => {
    return resp.data.data[0].stats;
  });
};

export const getStatsByYear = (
  context: Context,
  { pubId, params = {} }: { pubId: any; params: any },
) => {
  context.actions.publisher.checkAllSites({ params });
  const endpoint = `/api/v0/publishers/${pubId}/stats_by_year/`;
  return context.effects.api.get(endpoint, params).then(resp => {
    return resp.data.data[0].stats;
  });
};

export const getMerchantStatsByDay = (
  context: Context,
  { merchId, params = {} }: { merchId: any; params: any },
) => {
  const endpoint = `/api/v0/merchants/${merchId}/merch_ed/stats_by_day/`;
  context.actions.publisher.checkAllSites({ params });
  return context.effects.api.get(endpoint, params).then(resp => {
    return resp.data.data[0].stats;
  });
};

export const getMerchantStatsByMonth = (
  context: Context,
  { merchId, params = {} }: { merchId: any; params: any },
) => {
  const endpoint = `/api/v0/merchants/${merchId}/merch_ed/stats_by_month/`;
  context.actions.publisher.checkAllSites({ params });
  return context.effects.api.get(endpoint, params).then(resp => {
    return resp.data.data[0].stats;
  });
};

export const getMerchantStatsByYear = (
  context: Context,
  { merchId, params = {} }: { merchId: any; params: any },
) => {
  const endpoint = `/api/v0/merchants/${merchId}/merch_ed/stats_by_year/`;
  context.actions.publisher.checkAllSites({ params });
  return context.effects.api.get(endpoint, params).then(resp => {
    return resp.data.data[0].stats;
  });
};

export const getMerchantTopArticles = (
  context: Context,
  { merchId, params = {} }: { merchId: any; params: any },
) => {
  const endpoint = `/api/v0/merchants/${merchId}/merch_ed/top_articles/`;
  context.actions.publisher.checkAllSites({ params });
  return context.effects.api.get(endpoint, params).then(resp => {
    return resp.data.data[0].stats;
  });
};

export const getMerchantStatsByEdit = (
  context: Context,
  { merchId, params = {} }: { merchId: any; params: any },
) => {
  const endpoint = `/api/v0/merchants/${merchId}/merch_ed/stats_by_edit/`;
  context.actions.publisher.checkAllSites({ params });
  return context.effects.api.get(endpoint, params).then(resp => {
    return resp.data.data[0].stats;
  });
};

export const checkAllSites = (context: Context, { params }: { params: any }) => {
  const { sessionData } = context.state.session;
  const { getAccessListIds } = context.actions.session;

  if (sessionData.allSites) {
    const additionalOrgs = getAccessListIds();
    const role = sessionData.isAdmin
      ? sessionData.allPubs
        ? 'publishers'
        : 'merchants'
      : sessionData.userRole;
    if (additionalOrgs) {
      params[role === 'merchants' ? 'additional_merchs' : 'additional_pubs'] = additionalOrgs;
    }
  }
  return params;
};

export const getLifetimeEarnings = (
  context: Context,
  { pubId, params = {} }: { pubId: any; params: any },
) => {
  context.actions.publisher.checkAllSites({ params });
  const endpoint = `/api/v0/publishers/${pubId}/earnings_stats/`;
  return context.effects.api.get(endpoint, params).then(resp => {
    return resp.data.data[0].stats;
  });
};

/* Graph helpers */
export const generateDatesForRange = (
  context: Context,
  {
    startDate,
    endDate,
    timeUnit,
    rangeType,
  }: { startDate: any; endDate: any; timeUnit: any; rangeType: any },
) => {
  /*
   * Generate all dates for data points between two dates,
   * incremented by timeUnit (day or month)
   */
  const numDatesMap = {
    sevenDay: 7,
    halfYear: 6,
  };
  let numDates = numDatesMap[rangeType];

  if (!numDates) {
    /*
     * The day of month/month of year are the most accurate indicators for these ranges.
     * moment.duration returns a float based on how much of the day/month has elapsed,
     * and there's no consistent way to round it.
     */
    if (rangeType === 'thisYear') {
      numDates = endDate.month() + 1; // Months are 0-indexed
    } else if (rangeType === 'thisMonth' || rangeType === 'lastMonth') {
      numDates = endDate.date();
    } else {
      // Round down for partial year labeling
      numDates = Math.floor(
        context.actions.date.subtractDates({
          d1: startDate,
          d2: endDate,
          unit: timeUnit,
        }) as number,
      );
    }
  }

  const dates: any = [];
  for (let i = 0; i < numDates; i++) {
    const date = context.actions.date.offsetFromDate({
      originalDate: startDate,
      number: i,
      unit: timeUnit,
    });
    // API date format: 2019-10-01
    dates.push(context.actions.date.formatDate({ dateObj: date, fmt: 'Y-MM-DD' }));
  }

  return dates;
};

export const formatDateLabels = (
  context: Context,
  { statsWithLabels, timeUnit }: { statsWithLabels: any; timeUnit: any },
) => {
  /*
   * timeUnit: day|month|year
   * For example, if timeUnit was 'day', dateLabel would be:
   *   '30 Sep', '1 Oct', '2', '3', ...
   */
  const updatedStats: any = [];

  // Convert month from integer to text (1 => Jan)
  statsWithLabels.forEach((data, i) => {
    const dateFormat = 'D-MMM-Y'; // 1-jan-2019
    const formattedLabel = context.actions.date.formatDate({
      dateObj: data.dateLabel,
      fmt: dateFormat,
    });

    data.dateLabel = formattedLabel;
  });

  // Omit month or year if it's the same as the previous label's month/year
  statsWithLabels.forEach((data, i) => {
    const dateParts = data.dateLabel.split('-');
    const previousLabel = i > 0 ? statsWithLabels[i - 1].dateLabel : null;
    const previousParts = previousLabel ? previousLabel.split('-') : null;

    let label;
    let secondLabel;

    if (timeUnit === 'day') {
      if (!previousParts || previousParts[1] !== dateParts[1]) {
        secondLabel = dateParts[1]; // 'jan'
      }
      label = dateParts[0]; // '2'
    } else if (timeUnit === 'month') {
      if (!previousParts || previousParts[2] !== dateParts[2]) {
        secondLabel = dateParts[2]; // '2019'
      }
      label = dateParts[1]; // 'feb'
    } else if (timeUnit === 'year') {
      label = dateParts[2]; // '2019'
    }

    updatedStats.push({
      dateLabel: label,
      secondLabel,
      stats: data.stats,
    });
  });

  return updatedStats;
};

/* Helper functions (alphabetized) */
export const calculatePercentChange = (
  context: Context,
  { newStat, oldStat }: { newStat: any; oldStat: any },
) => {
  if (!oldStat) return '-';

  return Math.round(((newStat - oldStat) / oldStat) * 100);
};

export const displayPercentChange = (context: Context, value: any) => {
  // isNaN('-') evaluates to true => Display '-' if there's no stat
  return isNaN(value) ? value : `${Math.abs(value)}%`;
};

export const getRanking = (context: Context, change: any) => {
  if (change < 0) {
    return 'poor';
  }
  if (change >= 0 && change <= 5) {
    return 'fair';
  }
  if (change > 5) {
    return 'good';
  }
  // When there's no previous data to compare
  return 'neutral';
};
